import { Link, useMatch, useParams } from 'react-router-dom'

import type { ErrorLike } from '@apollo/client'
import { CombinedGraphQLErrors } from '@apollo/client/errors'

import { Button, CriticalIcon, EmptyState, Text } from '@buffer-mono/popcorn'

import { AccessDenied } from '~publish/components/AccessDenied'
import { useChannels } from '~publish/components/PublishSidebar/useChannels'
import * as routes from '~publish/legacy/routes'

import type { JSX } from 'react'

export function ChannelError({ error }: { error: ErrorLike }): JSX.Element {
  const { channels } = useChannels()
  const hasOnlyOneChannel = channels.length === 1
  const channelId = channels?.[0]?.id
  const isNewCalendarSingleChannel = useMatch(
    routes.newCalendarSingleChannel.route,
  )
  const isCommunitySingleChannel = useMatch(
    `${routes.communityChannel.baseRoute}/*`,
  )
  const params = useParams<{ granularity: 'week' | 'month' }>()

  let goBack = hasOnlyOneChannel
    ? {
        to: routes.channel.getRoute(channelId),
        label: 'Go back to Channel',
      }
    : {
        to: routes.allChannels.getRoute(),
        label: 'Go back to All Channels',
      }

  if (isNewCalendarSingleChannel) {
    goBack = hasOnlyOneChannel
      ? {
          to: routes.newCalendarSingleChannel.getRoute(
            channelId,
            params.granularity ?? 'week',
          ),
          label: 'Go back to Channel',
        }
      : {
          to: routes.newCalendarAllChannels.getRoute(
            params.granularity ?? 'week',
          ),
          label: 'Go back to All Channels',
        }
  }

  if (isCommunitySingleChannel) {
    goBack = {
      to: routes.communityPage.buildPathname(),
      label: 'Go back to All Channels',
    }
  }

  // Not-found is checked before permission: the legacy cross-org error carries
  // the FORBIDDEN code but must keep rendering as not-found (see
  // isNotFoundError).
  if (isNotFoundError(error)) {
    return (
      <EmptyState size="xlarge" variant="neutral">
        <EmptyState.Icon>
          <CriticalIcon />
        </EmptyState.Icon>
        <EmptyState.Heading>Channel not found</EmptyState.Heading>
        <EmptyState.Description>
          This channel does not exist
        </EmptyState.Description>
        <EmptyState.Actions>
          <Button as={Link} to={goBack.to} size="large">
            {goBack.label}
          </Button>
        </EmptyState.Actions>
      </EmptyState>
    )
  }
  if (isPermissionError(error)) {
    return (
      <AccessDenied
        description="You don’t have access to view this channel. Reach out to your organization owner to request access."
        action={goBack}
      />
    )
  }
  return (
    <EmptyState size="xlarge" variant="critical">
      <EmptyState.Icon>
        <CriticalIcon />
      </EmptyState.Icon>
      <EmptyState.Heading>Failed to load</EmptyState.Heading>
      <EmptyState.Description>
        Error happened, please let our team know about it.{' '}
        <Text color="critical">{error.message}</Text>
      </EmptyState.Description>
    </EmptyState>
  )
}

/**
 * The `extensions.code` values the backend attaches to every error thrown
 * through `declareResolver` (`throwGraphQLError` in
 * `graphql/src/_utils/errors.ts`): `UnauthorizedError` → `FORBIDDEN`,
 * `NotFoundError` → `NOT_FOUND`. Empty for transport failures.
 */
function getErrorCodes(error: ErrorLike): Set<unknown> {
  if (!CombinedGraphQLErrors.is(error)) {
    return new Set()
  }
  return new Set(error.errors.map((each) => each.extensions?.code))
}

// The message fallbacks cover errors that reach the client without their
// extensions (e.g. wrapped by gateway stitching).
function isPermissionError(error: ErrorLike): boolean {
  return (
    getErrorCodes(error).has('FORBIDDEN') ||
    error.message.startsWith('Failed to resolve hasActiveMemberDevice') ||
    // When the user does not have permissions on the channel
    error.message.includes("You don't have access to this channel")
  )
}

function isNotFoundError(error: ErrorLike): boolean {
  return (
    // When the channel ID is not found in the database
    getErrorCodes(error).has('NOT_FOUND') ||
    error.message.startsWith('Bad Request') || // When the channel ID is malformed
    // Thrown by the legacy getChannel use case for cross-org channel IDs; it
    // carries the FORBIDDEN code, but rendering it as access-denied would
    // reveal that a channel outside the actor's organizations exists — keep it
    // as not-found. The current `channel` query denies cross-org and same-org
    // access with one indistinguishable FORBIDDEN error (by design, see
    // buildChannelUnauthorizedError in core-modules), so this only narrows
    // errors from surfaces still on the legacy path.
    error.message.includes('Actor can not access this organization') ||
    error.message.includes('Channel not found')
  )
}
